import { useMemo, useState } from "react";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import { Link } from "react-router-dom";
import { useFollowUser } from "../../hooks/useFollowActions";
import useAuthentication from "../../hooks/useAuthentication";
import useGoToLogin from "../../hooks/useGoToLogin";
import Avatar from "../../common-components/Avatar";
import VerifiedBadge from "../../common-components/VerifiedBadge";
import { resolveImageUri } from "../../utility/constants";
import { buildUserPath } from "../../utility/utilityFunctions";

const AVATAR_SIZE = 72;

const PersonCard = ({ person, onFollowed }) => {
  const goToLogin = useGoToLogin();
  const { isLoggedIn } = useAuthentication();
  const { mutate: toggleFollow, isPending } = useFollowUser(person.id, {
    onSuccess: onFollowed,
  });

  const avatarUri = person.profilePhoto
    ? resolveImageUri(person.profilePhoto)
    : null;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        width: 108,
        flexShrink: 0,
        mr: 3,
      }}
    >
      <Box
        component={Link}
        to={buildUserPath(person)}
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textDecoration: "none",
          color: "inherit",
        }}
      >
        <Avatar src={avatarUri} name={person.name} size={AVATAR_SIZE} />
        <Typography
          variant="body2"
          sx={{
            fontSize: 12,
            textAlign: "center",
            mt: 0.75,
            color: "#374151",
            overflow: "hidden",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            lineHeight: 1.3,
          }}
        >
          {person.name}
          {person.isIdentityVerified && (
            <VerifiedBadge
              size={11}
              sx={{ verticalAlign: "middle", ml: "3px" }}
            />
          )}
        </Typography>
      </Box>
      <Button
        size="small"
        variant="outlined"
        disabled={isPending}
        onClick={() => {
          if (!isLoggedIn) {
            goToLogin();
            return;
          }
          toggleFollow(false);
        }}
        sx={{
          mt: 0.75,
          borderRadius: 5,
          fontSize: 11,
          minWidth: 64,
          py: 0.25,
          borderColor: "#046287",
          color: "#046287",
        }}
      >
        {isPending ? (
          <CircularProgress size={12} sx={{ color: "#046287" }} />
        ) : (
          "Follow"
        )}
      </Button>
    </Box>
  );
};

// Fixed snapshot passed down from the feed's recurrence cursor — this row
// never gets new props once rendered, so a followed person is removed from
// view locally rather than relying on invalidation to refresh the row.
const FeedPeopleRecommendationsRow = ({
  people,
  heading = "People to follow",
}) => {
  const [followedIds, setFollowedIds] = useState(() => new Set());

  const visiblePeople = useMemo(
    () =>
      (people ?? []).filter(
        (person) => !followedIds.has(person.id?.toString()),
      ),
    [people, followedIds],
  );

  if (!visiblePeople.length) return null;

  return (
    <Box sx={{ mb: 3 }}>
      <Typography
        variant="subtitle1"
        fontWeight={700}
        sx={{ mb: 1.5, color: "#111827" }}
      >
        {heading}
      </Typography>
      <Box
        sx={{
          display: "flex",
          overflowX: "auto",
          pb: 1,
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        {visiblePeople.map((person) => (
          <PersonCard
            key={person.id}
            person={person}
            onFollowed={() =>
              setFollowedIds((prev) => new Set(prev).add(person.id?.toString()))
            }
          />
        ))}
      </Box>
    </Box>
  );
};

export default FeedPeopleRecommendationsRow;
